import { useEffect, useRef } from "react";
import { Box } from "@mui/material";

const INK = "#12100d";

/* 玄関背景B — 星流れの空: 瞬く星・時折の流れ星・遠山の稜線 */
export default function ApproachB() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, raf = 0;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // 星（固定位置・明滅のみ）
    const N = 48;
    const stars = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: Math.random() * 0.72, // 空の領域だけ
      r: 0.4 + Math.random() * 1.1,
      tw: 0.25 + Math.random() * 0.9,
      phase: Math.random() * Math.PI * 2,
      warm: Math.random() < 0.55,
      a: 0.2 + Math.random() * 0.55,
    }));

    // 流れ星（一度にひとつ・数秒〜十数秒おき）
    let shooting = null;
    let nextShootAt = performance.now() + 3500 + Math.random() * 6000;
    const spawnShoot = (now) => {
      const fromX = 0.15 + Math.random() * 0.7;
      const fromY = 0.06 + Math.random() * 0.3;
      const ang = Math.PI * (0.72 + Math.random() * 0.16); // 右上→左下寄り
      shooting = { fromX, fromY, ang, born: now, life: 900 + Math.random() * 500, len: 90 + Math.random() * 70 };
    };

    let last = performance.now();
    const draw = (now) => {
      last = now;
      const t = now / 1000;
      ctx.clearRect(0, 0, w, h);

      for (const s of stars) {
        const tw = reduced ? 0.75 : 0.5 + 0.5 * Math.sin(t * s.tw * 2 + s.phase);
        const alpha = s.a * (0.35 + 0.65 * tw);
        const color = s.warm ? "222,215,198" : "212,180,110";
        const g = ctx.createRadialGradient(s.x * w, s.y * h, 0, s.x * w, s.y * h, s.r * 4);
        g.addColorStop(0, `rgba(${color},${alpha})`);
        g.addColorStop(1, `rgba(${color},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r * 4, 0, Math.PI * 2);
        ctx.fill();
      }

      if (!reduced) {
        if (!shooting && now >= nextShootAt) spawnShoot(now);
        if (shooting) {
          const p = (now - shooting.born) / shooting.life;
          if (p >= 1) {
            shooting = null;
            nextShootAt = now + 5000 + Math.random() * 9000;
          } else {
            const dist = p * (w * 0.35);
            const x = shooting.fromX * w + Math.cos(shooting.ang) * dist;
            const y = shooting.fromY * h - Math.sin(shooting.ang) * dist * -1;
            const tailX = x - Math.cos(shooting.ang) * shooting.len;
            const tailY = y + Math.sin(shooting.ang) * shooting.len * -1;
            const fade = Math.sin(Math.PI * p); // 出て消える
            const grad = ctx.createLinearGradient(tailX, tailY, x, y);
            grad.addColorStop(0, "rgba(222,215,198,0)");
            grad.addColorStop(1, `rgba(236,229,216,${0.65 * fade})`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(tailX, tailY);
            ctx.lineTo(x, y);
            ctx.stroke();
            const head = ctx.createRadialGradient(x, y, 0, x, y, 5);
            head.addColorStop(0, `rgba(236,229,216,${0.8 * fade})`);
            head.addColorStop(1, "rgba(236,229,216,0)");
            ctx.fillStyle = head;
            ctx.beginPath();
            ctx.arc(x, y, 5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        raf = requestAnimationFrame(draw);
      }
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: -1, overflow: "hidden", bgcolor: INK }}>
      {/* 夜空の基調 */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 110% 70% at 50% 0%, #221e18 0%, #191613 45%, ${INK} 100%)`,
        }}
      />

      {/* 天の川の気配（斜めの淡い帯） */}
      <Box
        sx={{
          position: "absolute",
          left: "-20%",
          top: "-10%",
          width: "140%",
          height: "70%",
          background:
            "linear-gradient(115deg, rgba(222,215,198,0) 30%, rgba(222,215,198,0.035) 46%, rgba(212,180,110,0.045) 52%, rgba(222,215,198,0.03) 58%, rgba(222,215,198,0) 72%)",
          filter: "blur(6px)",
          animation: "kodoMilky 80s ease-in-out infinite alternate",
          "@keyframes kodoMilky": {
            "0%": { transform: "translateX(-2%)" },
            "100%": { transform: "translateX(3%)" },
          },
        }}
      />

      {/* 星と流れ星（canvas） */}
      <Box component="canvas" ref={canvasRef} sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* 遠山の稜線（二層） */}
      <Box
        component="svg"
        viewBox="0 0 100 26"
        preserveAspectRatio="none"
        sx={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: "30vh" }}
      >
        <path
          d="M0,26 L0,15 Q12,10 22,13 Q34,16 44,11 Q56,6 68,12 Q82,18 100,12 L100,26 Z"
          fill="rgba(27,24,21,0.85)"
        />
        <path
          d="M0,26 L0,20 Q16,15 30,18 Q46,21 60,17 Q78,13 100,19 L100,26 Z"
          fill="rgba(14,12,10,0.95)"
        />
      </Box>

      {/* 地平の残光 */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: "18vh",
          transform: "translateX(-50%)",
          width: "70vw",
          height: "14vh",
          background: "radial-gradient(ellipse 50% 60% at 50% 100%, rgba(212,180,110,0.06), rgba(212,180,110,0) 70%)",
          filter: "blur(6px)",
        }}
      />

      {/* 手前の暗がり（文字の可読性） */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 120% 90% at 50% 115%, rgba(0,0,0,0.5), rgba(0,0,0,0) 55%)",
        }}
      />
    </Box>
  );
}
